import PropTypes from 'prop-types';
import React from 'react';
import { Form, TooltipHelp, TranslateDropdown } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { ToggleInheritedInput } from './InheritedInput';
import EditorComponent from '../../../../nacre/components/editorComponent';
import Styles from '../../../../styles/rate/rateCreation.module.css';
import { removeEmoji } from '../../../event_creation/utils';

const hasTranslations = (property, form) => form.translations && Object.keys(form.translations) && form.translations[property];

export function RateNameInput({
    event,
    rate,
    className,
    defaultLanguage,
    languageOptions,
    form,
    onChangeFormObject = () => {},
    setNameModified,
    handleTranslations = () => {},
    initLanguage = () => {}
}) {
    const { t } = useTranslation();
    const [selectedNameLanguage, setSelectedNameLanguage] = React.useState(defaultLanguage);

    const handleChange = React.useCallback(
        ({ target: { value } }) => {
            value = removeEmoji(value);
            handleTranslations(value, 'name', selectedNameLanguage);
            setValueLength(value.length);
        },
        [selectedNameLanguage, handleTranslations]
    );

    const maxLength = 60;

    const rateNameValue = React.useMemo(() => {
        if (!hasTranslations('name', form) || selectedNameLanguage === defaultLanguage) {
            return form.name;
        }
        return form.translations['name'][selectedNameLanguage] || '';
    }, [defaultLanguage, form, selectedNameLanguage]);

    const [valueLength, setValueLength] = React.useState(rateNameValue?.length || 0);

    const filledNameKeys = React.useMemo(() => {
        const filledDescriptions = [];
        if (form.name?.length) {
            filledDescriptions.push(defaultLanguage);
        }
        if (!hasTranslations('name', form)) {
            return filledDescriptions;
        }
        const allowedLanguages = languageOptions.map(l => l.value).filter(l => l !== defaultLanguage);
        let keysSelected = Object.keys(form.translations['name']).filter(k => form.translations['name'][k] !== '' && allowedLanguages.includes(k));
        return filledDescriptions.concat(keysSelected);
    }, [defaultLanguage, form, languageOptions]);

    return (
        <>
            <Form.Label>
                <div className={`${Styles['rate-name-input']} ${className}`}>
                    <span>
                        {t(`ticket.rates.infos.name`)}
                        <TooltipHelp title={t('ticket.rates.infos.nameTooltip')} />
                    </span>
                    <span className={Styles['rate-name-input']}>
                        {form.name && event && (
                            <TranslateDropdown
                                defaultValue={defaultLanguage}
                                languages={languageOptions}
                                onChange={value => {
                                    setSelectedNameLanguage(value);
                                    initLanguage(value, 'name');
                                }}
                                selected={filledNameKeys}
                            />
                        )}
                        {rate?.rate_template && (
                            <ToggleInheritedInput
                                rate={rate}
                                form={form}
                                onChange={changesName => {
                                    onChangeFormObject({ ...changesName }, true);
                                    setNameModified(true);
                                }}
                                property="name"
                            />
                        )}
                    </span>
                </div>
            </Form.Label>

            <Form.Input
                required
                name="name"
                placeholder={t(`ticket.rates.infos.placeholderName`)}
                value={rateNameValue}
                disabled={rate?.rate_template && !form.name}
                onChange={handleChange}
            />
            <span className={Styles['input-length']}>
                <span>
                    {valueLength || 0}
                    {`/${maxLength}`}
                </span>
                {valueLength > 60 && <span className={Styles['input-length-message']}>{t('ticket.rates.infos.name-too-long')}</span>}
            </span>
        </>
    );
}

RateNameInput.propTypes = {
    event: PropTypes.object,
    rate: PropTypes.object,
    defaultLanguage: PropTypes.string.isRequired,
    languageOptions: PropTypes.array.isRequired,
    form: PropTypes.object,
    initLanguage: PropTypes.func,
    handleTranslations: PropTypes.func,
    setNameModified: PropTypes.func
};

export function RateDescriptionInput({ event, rate, defaultLanguage, languageOptions, form, handleTranslations = () => {}, initLanguage = () => {} }) {
    const { t } = useTranslation();
    const [selectedDescriptionLanguage, setSelectedDescriptionLanguage] = React.useState(defaultLanguage);

    const _handleTranslations = React.useCallback(
        (value, property) => {
            return handleTranslations(value, property, selectedDescriptionLanguage);
        },
        [selectedDescriptionLanguage, handleTranslations]
    );

    const rateDescriptionValue = React.useMemo(() => {
        if (!hasTranslations('description', form) || selectedDescriptionLanguage === defaultLanguage) {
            return form.description;
        }
        return form.translations['description'][selectedDescriptionLanguage] || '';
    }, [defaultLanguage, form, selectedDescriptionLanguage]);

    const filledDescriptionKeys = React.useMemo(() => {
        const filledDescriptions = [];
        if (form.description?.length) {
            filledDescriptions.push(defaultLanguage);
        }
        if (!hasTranslations('description', form)) {
            return filledDescriptions;
        }
        const allowedLanguages = languageOptions.map(l => l.value).filter(l => l !== defaultLanguage);
        let keysSelected = Object.keys(form.translations['description']).filter(k => form.translations['description'][k] !== '' && allowedLanguages.includes(k));
        return filledDescriptions.concat(keysSelected);
    }, [defaultLanguage, form, languageOptions]);

    return (
        <>
            <Form.Label>
                <div className={Styles['rate-name-input']}>
                    <span>
                        {t('ticket.rates.infos.labelDescription')}
                        <TooltipHelp title={t('ticket.rates.infos.descriptionTooltip')} />
                        <span className={Styles['optional']}>{t('ticket.common.labelOptional')}</span>
                    </span>
                    {event && (
                        <TranslateDropdown
                            defaultValue={defaultLanguage}
                            languages={languageOptions}
                            onChange={value => {
                                setSelectedDescriptionLanguage(value);
                                initLanguage(value, 'description');
                            }}
                            selected={filledDescriptionKeys}
                        />
                    )}
                </div>
            </Form.Label>
            <Form.Sublabel label={t('ticket.rates.infos.sublabelDescription')} />

            <EditorComponent
                description={rateDescriptionValue}
                onChange={(content, editor) => _handleTranslations(removeEmoji(content), 'description')}
                placeholder={t('ticket.rates.infos.placeholderDescription')}
                height={300}
                menubar={false}
            />
        </>
    );
}

RateDescriptionInput.propTypes = {
    event: PropTypes.object,
    defaultLanguage: PropTypes.string.isRequired,
    languageOptions: PropTypes.array.isRequired,
    form: PropTypes.object,
    handleTranslations: PropTypes.func,
    initLanguage: PropTypes.func
};
